import { useCallback } from 'react'
import type { FC } from 'react'
import {
	Heading,
	Box,
	Button,
	Text,
	Grid,
	useDisclosure,
	Modal,
	ModalOverlay,
	ModalContent,
	ModalHeader,
	ModalCloseButton,
	ModalBody,
	Flex,
	FormControl,
	Input,
	Textarea,
	Table,
	TableContainer,
	TableCaption,
	Thead,
	Tbody,
	Tr,
	Td,
	Th,
	Collapse,
	FormErrorMessage,
	FormLabel,
} from '@chakra-ui/react'
import type { ChakraProps, ThemingProps } from '@chakra-ui/react'
import {
	Feature,
	ContentSection,
	ContentSectionHeading,
} from '@alexanderathoodly/ui-library'
import type { ConferenceRoomResult } from '@alexanderathoodly/data-models'
import {
	conferenceRoomsByLocation,
	useYtaQuery,
	createConferenceRoomRequest,
	useYtaMutation,
	providerById,
} from '@alexanderathoodly/data-models'
import { useLocationPage } from './context'
import { useTranslation } from 'react-i18next'
import {
	PLACEHOLDER_SRC,
	SIZE_410,
	SIZE_290,
	SIZE_1070,
} from '@yta/image-proxy'
import { ImgProxy } from '~/utils/imgproxy'
import { useForm, SubmitHandler } from 'react-hook-form'
import { truncate } from '~/utils/text'
import { sendGA4Event } from '~/analytics/ga'

const ConferenceInterestForm: FC<{
	room: ConferenceRoomResult
	onSubmitSuccess: () => void
}> = ({ room, onSubmitSuccess }) => {
	const { t } = useTranslation()
	const { location } = useLocationPage()
	const { data: provider } = useYtaQuery(
		providerById({ id: location?.provider })
	)

	const requestMutation = useYtaMutation(createConferenceRoomRequest, {})

	const onSubmit = useCallback<
		SubmitHandler<{
			email: string
			phone_number: string
			message: string
		}>
	>(
		(formData) => {
			const data = { conference_room: room.id, ...formData }
			requestMutation.mutate(
				{ data },
				{
					onSuccess: () => {
						sendGA4Event('request_conference', {
							operator: provider?.name || 'failed_to_fetch_provider',
							location: location?.name || 'failed_to_fetch_location',
						})
					},
				}
			)
		},
		[requestMutation, room.id, provider?.name, location?.name]
	)

	const {
		register,
		formState: { errors },
		handleSubmit,
	} = useForm<{
		email: string
		phone_number: string
		message: string
	}>({
		mode: 'onBlur',
		defaultValues: {
			email: localStorage.getItem('email') || '',
			phone_number: localStorage.getItem('phoneNumber') || '',
			message: '',
		},
	})

	return requestMutation.isSuccess ? (
		<Box mx="auto" py={7}>
			<Text>
				{t('Din förfrågan har skickats till {{locationName}}', {
					locationName: location?.name || t('ansvarig'),
				})}
			</Text>
			<Button onClick={onSubmitSuccess} mt={5} w="full">
				{t('Stäng')}
			</Button>
		</Box>
	) : (
		<Flex
			as="form"
			onSubmit={handleSubmit(onSubmit)}
			flexDirection="column"
			gap={2}
		>
			<Text>
				{t(
					'Gör en förfrågan direkt till hyresvärden om att boka ett mötes- eller konferensrum för ditt kommande event så återkommer hyresvärden till dig med mer information.'
				)}
			</Text>
			<FormControl isInvalid={!!errors.phone_number}>
				<FormLabel color="grey.500" fontSize="sm">
					{t('Mobilnummer')}
				</FormLabel>
				<Input
					type="tel"
					placeholder={t('Mobiltelefonnummer')}
					{...register('phone_number', {
						required:
							t('Mobiltelefonnummer är nödvändigt.') ||
							'Mobiltelefonnummer är nödvändigt.',
					})}
				/>
				{errors.phone_number && (
					<FormErrorMessage>{errors.phone_number.message}</FormErrorMessage>
				)}
			</FormControl>
			<FormControl isInvalid={!!errors.email}>
				<FormLabel color="grey.500" fontSize="sm">
					{t('E-post')}
				</FormLabel>
				<Input
					type="email"
					placeholder={t('E-post')}
					{...register('email', {
						required: t('Email är nödvändig.') || 'Email är nödvändig.',
					})}
				/>
				{errors.email && (
					<FormErrorMessage>{errors.email.message}</FormErrorMessage>
				)}
			</FormControl>
			<FormControl>
				<Text color="grey.500" fontSize="sm" mt={2}>
					{t('Meddelande')}
				</Text>
				<Textarea placeholder={t('Meddelande')} {...register('message')} />
			</FormControl>
			{requestMutation.error && (
				<Text color="red">
					{requestMutation.error.message || t('Något gick fel')}
				</Text>
			)}
			<Button w="full" type="submit" my={3}>
				{t('Skicka förfrågan')}
			</Button>
		</Flex>
	)
}

const ConferenceInterestButton: FC<
	{ room: ConferenceRoomResult } & ChakraProps & ThemingProps
> = ({ room, ...rest }) => {
	const { t } = useTranslation()
	const { isOpen, onOpen, onClose } = useDisclosure()

	const { location } = useLocationPage()
	const { data: provider } = useYtaQuery(
		providerById({ id: location?.provider })
	)

	return (
		<>
			<Button
				onClick={() => {
					sendGA4Event('initiate_conference_request', {
						operator: provider?.name || 'failed_to_fetch_provider',
						location: location?.name || 'failed_to_fetch_location',
					})
					onOpen()
				}}
				{...rest}
			>
				{t('Förfrågan')}
			</Button>
			<Modal isOpen={isOpen} onClose={onClose}>
				<ModalOverlay />
				<ModalContent maxW="md">
					<ModalHeader>{t('Vill du boka konferens?')}</ModalHeader>
					<ModalCloseButton />
					<ModalBody display="flex">
						<ConferenceInterestForm room={room} onSubmitSuccess={onClose} />
					</ModalBody>
				</ModalContent>
			</Modal>
		</>
	)
}

const PriceListFeature: FC<{ room: ConferenceRoomResult }> = ({ room }) => {
	const { t } = useTranslation()
	const { isOpen, onOpen, onClose } = useDisclosure()

	if (!room.price_list.length) {
		return <Feature name={t('Pris')} value={t('På förfrågan')} />
	}

	return (
		<>
			<Feature
				name={t('Pris')}
				value={
					<Text
						onClick={onOpen}
						color="blue.500"
						_hover={{ cursor: 'pointer', textDecoration: 'underline' }}
					>
						{t('Visa prislista')}
					</Text>
				}
			/>
			<Modal isOpen={isOpen} onClose={onClose}>
				<ModalOverlay />
				<ModalContent maxW="md">
					<ModalHeader>{t('Prislista')}</ModalHeader>
					<ModalCloseButton />
					<ModalBody display="flex" flexDirection="column">
						<TableContainer>
							<Table variant="simple">
								<TableCaption>
									{t(
										'Konferens {{capacity}} personer • {{conference_room_name}}',
										{
											capacity: room.capacity,
											conference_room_name: room.name,
										}
									)}
								</TableCaption>
								<Thead>
									<Tr>
										<Th isNumeric>{t('Pris')}</Th>
										<Th>{t('Per')}</Th>
									</Tr>
								</Thead>
								<Tbody>
									{room.price_list.map((p, i) => (
										<Tr key={`price-list-row${i}`}>
											<Td isNumeric>{p.price.toLocaleString('sv-SE')}</Td>
											<Td>{p.unit}</Td>
										</Tr>
									))}
								</Tbody>
							</Table>
						</TableContainer>
					</ModalBody>
				</ModalContent>
			</Modal>
		</>
	)
}

const ConferenceRoomItem: FC<
	{ room: ConferenceRoomResult; isLast: boolean } & ChakraProps
> = ({ room, isLast, ...rest }) => {
	const { t, i18n } = useTranslation()
	const { isOpen, onToggle } = useDisclosure()
	const { isOpen: isRoomImagesOpen, onOpen, onClose } = useDisclosure()

	const { location } = useLocationPage()

	const MAX_DESCRIPTION_LENGTH = 70
	const description =
		i18n.language != 'en' ? room.description : room.description_en
	const isImageExist = room.images.length > 0
	return (
		<Grid
			as="article"
			templateColumns="auto 1fr auto"
			templateRows={{ base: 'auto', md: 'repeat(3, min-height)' }}
			autoFlow="row"
			borderColor="grey.100"
			borderStyle="solid"
			borderBottomWidth={isLast ? 0 : 'thin'}
			py={3}
			gap="10px"
			{...rest}
		>
			<Heading
				as="h3"
				fontSize="md"
				fontWeight="semibold"
				gridColumn={{ base: 'span 3', md: 'span 2' }}
			>
				{t('Konferens {{capacity}} personer • {{conference_room_name}}', {
					capacity: room.capacity,
					conference_room_name: room.name,
				})}
			</Heading>
			{isImageExist && (
				<Box position="relative" gridColumn="1">
					<ImgProxy
						sizeSet={{ base: SIZE_290 }}
						src={room.images[0] || ''}
						loading="lazy"
						height={16}
						width="min-content"
						alt={`${t('Konferensrum')}: ${room.name} - ${location?.name || ''}`}
					/>
				</Box>
			)}
			<Flex
				alignItems="center"
				gap={{ base: 5, md: 10 }}
				gridColumn={{ base: 'auto', md: isImageExist ? '2/3' : '1/2' }}
				gridRow={{ base: 'auto', md: '2/3' }}
			>
				<Feature
					name={t('Platser')}
					value={room.capacity.toLocaleString('sv-SE')}
				/>
				<PriceListFeature room={room} />
			</Flex>
			<Text
				gridColumn={{ base: '1/-1', md: '1/3' }}
				gridRow={{ md: isImageExist ? 'auto' : '3/4' }}
				mt={3}
				mb={6}
			>
				{isOpen ? description : truncate(description, MAX_DESCRIPTION_LENGTH)}
			</Text>
			{isImageExist && (
				<Flex
					display={isOpen ? 'flex' : 'none'}
					gridColumn={{ base: '1/-1', md: '1/4' }}
					wrap="wrap"
					gap={{ base: '6px', md: '10px' }}
					hidden={!isOpen}
					direction="row"
					position="relative"
					mb={6}
				>
					{room.images.map((image, index) => (
						<ImgProxy
							width="32%"
							key={`${image}-${index}`}
							sizeSet={{ base: SIZE_290 }}
							src={image || PLACEHOLDER_SRC}
							loading="lazy"
							alt={`room-${image}-${index}`}
							_hover={{ cursor: 'pointer' }}
							onClick={() => {
								onOpen()
								// Using timeout just to give the image element to be in the dom once the modal opens,
								// 'getElementById' was smoother than having ref's for all images.
								setTimeout(() => {
									const imageElement = document.getElementById(
										`solution-image-${image}`
									)
									if (!imageElement) {
										return
									}
									imageElement.scrollIntoView({
										behavior: 'smooth',
										block: 'end',
									})
								}, 300)
							}}
						/>
					))}
					<Modal isOpen={isRoomImagesOpen} onClose={onClose}>
						<ModalOverlay />
						<ModalContent maxW="6xl">
							<ModalHeader>{t('Bilder')}</ModalHeader>
							<ModalCloseButton />
							<ModalBody display="flex" flexDirection="column" gap="50px">
								{room.images.map((image, index) => (
									<ImgProxy
										id={`solution-image-${image}`}
										key={image}
										src={image}
										sizeSet={{ base: SIZE_410, md: SIZE_1070 }}
										w="100%"
										alt={`room-${image}-${index}`}
									/>
								))}
							</ModalBody>
						</ModalContent>
					</Modal>
				</Flex>
			)}
			<ConferenceInterestButton
				room={room}
				variant={isOpen ? 'solid' : 'outline'}
				gridColumn={{ base: '1/-1', md: '3/4' }}
				gridRow={{ base: '4/5', md: '2/3' }}
				mb={2}
			/>
			{(isImageExist || description.length > MAX_DESCRIPTION_LENGTH) && (
				<Button
					variant="link"
					color="blue.500"
					size="sm"
					onClick={() => {
						onToggle()
					}}
					gridRow={{ base: 'auto', md: '3/4' }}
					gridColumn={{ base: '1/-1', md: '-2/-1' }}
					justifySelf="end"
					width={{
						base: '100%',
						md: 'min-content',
					}}
				>
					{isOpen ? t('Visa mindre') : t('Visa mer')}
				</Button>
			)}
		</Grid>
	)
}

export const ConferenceSection: FC = ({ ...rest }) => {
	const { t } = useTranslation()
	const { location } = useLocationPage()
	const { isOpen, onToggle } = useDisclosure()

	const { data: rooms } = useYtaQuery(
		conferenceRoomsByLocation({
			location: location?.id || '',
		})
	)

	return location?.is_premium_listing &&
		location.conference_room_request_email &&
		rooms &&
		rooms?.length > 0 ? (
		<ContentSection id="conference" {...rest}>
			<ContentSectionHeading>
				{t('Bokningsbara konferensrum')}
			</ContentSectionHeading>
			<Text>
				{t(
					'Konferensrum som finns tillgängliga att hyra som hyresgäst eller extern kund hos {{location_name}}. Gör en förfrågan nedan för att komma i kontakt med hyresvärden för att boka ett mötesrum för din nästa konferens.',
					{ location_name: location.name }
				)}
			</Text>
			<Box mt={3}>
				{rooms.slice(0, 3).map((room, index) => (
					<ConferenceRoomItem
						key={room.id}
						room={room}
						isLast={index + 1 == rooms.length}
						mb={5}
					/>
				))}
				{rooms.length > 3 && (
					<>
						<Collapse in={isOpen}>
							{rooms.slice(3).map((room, index) => (
								<ConferenceRoomItem
									key={room.id}
									room={room}
									isLast={index + 1 == rooms.length}
									mb={5}
								/>
							))}
						</Collapse>
						<Button variant="link" mt={2} w="100%" onClick={onToggle}>
							{isOpen
								? t('Visa färre konferensrum')
								: t(
										'Visa {{remaining_conference_rooms}} ytterligare konferensrum',
										{
											remaining_conference_rooms: rooms.slice(3).length,
										}
								  )}
						</Button>
					</>
				)}
			</Box>
		</ContentSection>
	) : null
}
